import { HTMLAttributes } from 'react'
import Image from 'next/image'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { CardBorder } from './grid'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type CardModel = {
  image: WPImage
  name: string
  role: string
}

export type CardProps = HTMLAttributes<HTMLDivElement> & CardModel

export const Card = ({ image, name, role, className, ...props }: CardProps) => {
  return (
    <div className={cn('grid h-full', className)} {...props}>
      <div className="pointer-events-none relative z-10 col-[1/-1] row-[1/-1] flex items-start">
        <CardBorder size={320} className="h-auto w-full" />
      </div>
      <div className="pointer-events-none relative z-10 col-[1/-1] row-[1/-1] flex items-end">
        <CardBorder size={320} className="h-auto w-full rotate-180" />
      </div>

      <div
        className={cn(
          'relative col-[1/-1] row-[1/-1]',
          'rounded-[0.7rem] bg-white-light/40 backdrop-blur-sm dark:bg-white-dark/40'
        )}
      >
        <div className="flex h-full w-full flex-col p-spacing-sm">
          <div className={cn('grid gap-y-spacing-sm pb-spacing-sm')}>
            {image && image.url ? (
              <Image
                src={image.url}
                alt={image.alt || name || ''}
                width={image.width}
                height={image.height}
                className="aspect-[1] w-full rounded-radius-minimal object-cover"
              />
            ) : null}

            {name ? (
              <div className="flex">
                <Typography variant="large" className="font-medium">
                  {name}
                </Typography>
              </div>
            ) : null}

            {role ? (
              <div className="flex">
                <Typography variant="body">{role}</Typography>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export type GridTeamProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    team: CardModel[]
  }>

const GridTeam = ({ team, ...props }: GridTeamProps) => {
  return (
    <Section {...props}>
      <Container>
        <Grid className="gap-y-spacing-sm md:gap-y-gutter">
          {team?.map((card, index) => (
            <Card
              key={index}
              className="col-span-2 md:col-span-3 lg:col-span-4"
              data-motion-card
              {...card}
            />
          ))}
        </Grid>
      </Container>
    </Section>
  )
}

export default GridTeam
